import React, { useCallback } from 'react';

import { RenewalReviewRequestBlock, SupportAction, SupportTicketFormBlock } from '../../../types';
import { ResponseActions } from '../../ResponseActionButton';
import { useResponseAction } from '../../responseActions';
import {
  ResponseHead,
  ResponseIntro,
  ResponsePanel,
  ResponsePanelBody,
  ResponseReply
} from '../../responsePrimitives';
import { ResponseFormFields, useResponseFormValues } from './ResponseFormFields';
import { ResponseResultStatus } from './ResponseFormResults';

type ResponseFormBlock = SupportTicketFormBlock | RenewalReviewRequestBlock;

export interface IResponseFormProps<TBlock extends ResponseFormBlock> {
  block: TBlock;
  /** The card or banner a non-draft status draws under the panel. */
  renderResult: (
    status: ResponseResultStatus,
    result: NonNullable<TBlock['result']>,
    onAction: (action: SupportAction) => void
  ) => React.ReactNode;
}

/**
 * The intro, the titled panel of fields and its actions, then the result. Every action carries
 * every field's current value, keyed by `field.key`.
 */
export const ResponseForm = <TBlock extends ResponseFormBlock>({
  block,
  renderResult
}: IResponseFormProps<TBlock>) => {
  const emit = useResponseAction(block);
  const [values, setValue] = useResponseFormValues(block.fields);
  const onAction = useCallback((action: SupportAction) => emit(action, values), [emit, values]);
  const { status, result } = block;
  return (
    <ResponseReply
      data-testid={`orbit-response-${block.type}`}
      data-block-id={block.id}
      data-status={status}
    >
      {block.intro ? <ResponseIntro>{block.intro}</ResponseIntro> : null}
      <ResponsePanel>
        <ResponseHead title={block.title} icon={block.icon} />
        <ResponsePanelBody>
          <ResponseFormFields
            fields={block.fields}
            values={values}
            locked={status !== 'draft'}
            onChange={setValue}
          />
          <ResponseActions actions={block.actions} onAction={onAction} />
        </ResponsePanelBody>
      </ResponsePanel>
      {status !== 'draft' && result
        ? renderResult(status, result as NonNullable<TBlock['result']>, onAction)
        : null}
    </ResponseReply>
  );
};
